// Context-window meter beside Send (ported from Build's ContextMeter).
// Reads the CLI status-line snapshot via GET /api/sessions/:id/context. Total
// and model label always come from the snapshot (1M for Opus 5.5 today) —
// never hard-coded. The popover offers native Compact when the running
// process supports it (server `compactable`); the verdict comes from server
// state, never from the button's request. Hidden until the chat has real data.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ContextUsage } from "../lib/types";
import { getContextUsage } from "../store/api";
import { useSession } from "../store/session";

interface ContextMeterProps {
  chatId: string | null;
  isStreaming: boolean;
}

function formatTokens(value: number | null): string {
  if (value === null) return "—";
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${Number.isInteger(millions) ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(value);
}

function updatedAgo(iso: string | null, now: number): string | null {
  const at = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(at)) return null;
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  if (minutes < 1) return "updated just now";
  if (minutes < 60) return `updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 48 ? `updated ${hours}h ago` : `updated ${Math.floor(hours / 24)}d ago`;
}

export default function ContextMeter({ chatId, isStreaming }: ContextMeterProps) {
  const [usageState, setUsageState] = useState<{ chatId: string; usage: ContextUsage | null } | null>(null);
  // Keyed by chat so switching chats closes the popover without a reset effect.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const open = openFor !== null && openFor === chatId;
  const [now, setNow] = useState(() => Date.now());
  const generation = useRef(0);
  const wasStreaming = useRef(isStreaming);
  const rootRef = useRef<HTMLDivElement>(null);
  const compactable = useSession((s) => (chatId ? s.sessionStatusByCompany[chatId]?.compactable === true : false));
  const compaction = useSession((s) => (chatId ? s.compactionByCompany[chatId] : undefined));
  const compactCompany = useSession((s) => s.compactCompany);
  const compacting = compaction?.state === "compacting";

  const refresh = useCallback(async () => {
    if (!chatId) return;
    const currentGeneration = generation.current;
    try {
      const next = await getContextUsage(chatId);
      if (currentGeneration === generation.current) {
        setUsageState({ chatId, usage: next });
        setNow(Date.now());
      }
    } catch {
      // Optional telemetry: a transient fetch/auth failure must never
      // interfere with composing or sending; keep the last truthful snapshot.
    }
  }, [chatId]);

  useEffect(() => {
    // Stale snapshots from the previous chat are ignored by the chatId check below.
    generation.current += 1;
    if (!chatId) return;
    // First read on the next tick (never setState synchronously in the effect).
    const first = window.setTimeout(() => void refresh(), 0);
    const interval = window.setInterval(() => void refresh(), 15_000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(interval);
    };
  }, [chatId, refresh]);

  useEffect(() => {
    const justFinished = wasStreaming.current && !isStreaming;
    wasStreaming.current = isStreaming;
    if (!justFinished || !chatId) return;
    // The reply can land just before Claude redraws its status line.
    const first = window.setTimeout(() => void refresh(), 250);
    const settled = window.setTimeout(() => void refresh(), 1_500);
    return () => {
      window.clearTimeout(first);
      window.clearTimeout(settled);
    };
  }, [chatId, isStreaming, refresh]);

  // A finished compaction changes the figure: re-read it (the backend shows
  // the post-compaction size until the next reply).
  const compactionKey = compaction ? `${compaction.state}:${compaction.finishedAt ?? ""}` : "";
  useEffect(() => {
    if (!chatId || !compactionKey.startsWith("done")) return;
    const timer = window.setTimeout(() => void refresh(), 150);
    return () => window.clearTimeout(timer);
  }, [chatId, compactionKey, refresh]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpenFor(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const usage = usageState?.chatId === chatId ? usageState.usage : null;
  const percent = usage?.usedPercentage ?? (
    usage?.usedTokens != null && usage.totalTokens ? (usage.usedTokens / usage.totalTokens) * 100 : null
  );
  // An empty ring reads like a stuck spinner: stay hidden until real data.
  if (!usage || percent === null) return null;

  const used = Math.max(0, Math.min(100, percent));
  const tone = used >= 90 ? "danger" : used >= 75 ? "warn" : "normal";
  const ago = updatedAgo(usage.updatedAt, now);

  return (
    <div ref={rootRef} className={`contextMeter contextMeter--${tone} ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="contextMeter__button"
        onClick={() => {
          setOpenFor(open ? null : chatId);
          void refresh();
        }}
        aria-label={`Context window: ${Math.round(used)} percent used`}
        aria-expanded={open}
        aria-controls="context-meter-popover"
      >
        <svg className="contextMeter__ring" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="contextMeter__track" cx="12" cy="12" r="8.5" />
          <circle
            className="contextMeter__value"
            cx="12"
            cy="12"
            r="8.5"
            pathLength="100"
            strokeDasharray="100"
            strokeDashoffset={100 - used}
          />
        </svg>
      </button>

      <div id="context-meter-popover" className="contextMeter__popover" role="status">
        <div className="contextMeter__title">Context window</div>
        <div className="contextMeter__headline">{Math.round(used)}% used</div>
        <div className="contextMeter__tokens">
          {formatTokens(usage.usedTokens)} / {formatTokens(usage.totalTokens)} tokens
        </div>
        {(usage.modelLabel || usage.modelId) && (
          <div className="contextMeter__model">{usage.modelLabel || usage.modelId}</div>
        )}
        {ago && <div className="contextMeter__updated">{ago}</div>}
        {(compactable || compacting) && (
          <div className="contextMeter__compact">
            <button
              type="button"
              className="contextMeter__compactButton"
              onClick={() => { if (chatId) void compactCompany(chatId); }}
              disabled={!chatId || isStreaming || compacting}
              title={isStreaming && !compacting ? "Available when the current reply finishes" : undefined}
            >
              {compacting ? "Compacting…" : "Compact context"}
            </button>
            <div className="contextMeter__compactNote" aria-live="polite">
              {compacting
                ? "Usually 20 s – 3 min. Keep chatting: messages wait and send after."
                : usage.source === "compact_boundary" && usage.preCompactTokens != null
                  ? `Compacted: ${formatTokens(usage.preCompactTokens)} → ${formatTokens(usage.usedTokens)} tokens. Showing the size right after it.`
                  : "Summarises this chat so far into a shorter context."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
